import { View, Button } from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useWindowDimensions } from "@/components/hooks/use-window-dimension";
import { useState } from "react";
import { DotsThreeOutline } from "@/components/shared/icons";
import { BottomDrawerModal } from "@/components/shared/bottom-drawer-modal";
import { DropdownMenuGroup } from "@radix-ui/react-dropdown-menu";
import { useUpdateAddressMutation } from "@/gqlhooks/profile/useUpdateAddressMutation";
import { useClientAddressesQuery } from "@/gqlhooks/profile/useClientAddressesQuery";
import { useApolloClient } from "@apollo/client";
import { useDeleteAddressMutation } from "@/gqlhooks/profile/useDeleteAddressMutation";
import { showToast } from "@/components/ui/toast/show-toast";
import { getErrorMessage } from "@/lib/helpers/string";

export const AddressMoreActions = ({ addressId }: { addressId: string }) => {
  const { data: addresses } = useClientAddressesQuery();
  const client = useApolloClient();
  const { isMobile } = useWindowDimensions();
  const [openMoreActions, setOpenMoreActions] = useState<boolean>(false);

  const [updateAddressMutation, { loading: updateLoading }] =
    useUpdateAddressMutation();

  const [deleteAddressMutation, { loading: deleteLoading }] =
    useDeleteAddressMutation();

  const loading = updateLoading || deleteLoading;

  const handleSetPrimaryAddress = async () => {
    const existingAddresses = addresses || [];

    const updatedPrimaryAddress = existingAddresses.find(
      (add) => add.id === addressId,
    );

    try {
      if (updatedPrimaryAddress?.id) {
        await updateAddressMutation({
          variables: {
            input: {
              id: updatedPrimaryAddress.id,
              fullAddress: updatedPrimaryAddress.fullAddress,
              postalCode: updatedPrimaryAddress.postalCode,
              unitNumber: updatedPrimaryAddress.unitNumber,
              primary: true,
            },
          },
        });
      }

      // Evict and refetch the addresses cache
      client.cache.evict({ fieldName: "clientAddresses" });
      client.cache.gc();

      showToast({
        title: "Success update addresses",
        type: "success",
      });
    } catch (error) {
      const errMsg = getErrorMessage(error, "Failed to update addresses");
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  };

  const handleClose = () => {
    setOpenMoreActions(false);
  };

  const handleDeleteAddress = async () => {
    try {
      const result = await deleteAddressMutation({
        variables: {
          input: {
            id: addressId,
          },
        },
      });

      if (result.data?.deleteAddress?.result && !loading) {
        // Evict and refetch the addresses cache
        client.cache.evict({ fieldName: "clientAddresses" });
        client.cache.gc();
        handleClose();
        showToast({
          title: "Success delete addresses",
          type: "success",
        });
      }
    } catch (error) {
      const errMsg = getErrorMessage(error, "Error delete address");
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  };

  return (
    <>
      <DropdownMenu onOpenChange={setOpenMoreActions}>
        <DropdownMenuTrigger>
          <View className="flex items-center rounded-full border border-primary-border p-2">
            <DotsThreeOutline
              weight="duotone"
              className="size-5 text-primary"
            />
          </View>
        </DropdownMenuTrigger>
        {!isMobile && (
          <DropdownMenuContent className="-left-10 p-2 px-3">
            <DropdownMenuGroup>
              <DropdownMenuItem>
                <Button
                  variant="tertiary"
                  color="foreground"
                  loading={loading}
                  className="items-start gap-3 rounded-none border-b border-secondary-border p-0 pb-2"
                  onClick={handleSetPrimaryAddress}
                  iconName="mapPinLine"
                  children="Set as default Address"
                />
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Button
                  variant="secondary"
                  color="danger"
                  loading={loading}
                  className="items-start gap-3 border-0 p-0"
                  onClick={handleDeleteAddress}
                  iconName="trash"
                  iconColor="danger"
                  children="Delete Address"
                />
              </DropdownMenuItem>
            </DropdownMenuGroup>
          </DropdownMenuContent>
        )}
      </DropdownMenu>
      {isMobile && (
        <BottomDrawerModal
          open={openMoreActions}
          onOpenChange={setOpenMoreActions}
        >
          <View className="flex flex-col gap-4">
            <Button
              variant="tertiary"
              color="foreground"
              fullWidth="full"
              loading={loading}
              className="justify-start gap-4 rounded-none border-b border-secondary-border p-0 pb-5"
              onClick={handleSetPrimaryAddress}
              iconName="mapPinLine"
              children="Set as default Address"
            />

            <Button
              variant="secondary"
              color="danger"
              loading={loading}
              fullWidth="full"
              className="justify-start gap-4 border-0 p-0"
              onClick={handleDeleteAddress}
              iconName="trash"
              iconColor="danger"
              children="Delete Address"
            />

            <Button
              variant="primary"
              color="CTA"
              fullWidth="full"
              onClick={handleClose}
              loading={loading}
              children="Close"
            />
          </View>
        </BottomDrawerModal>
      )}
    </>
  );
};
